// FeetInchesInput.tsx — 3-box dimension input: [◄ feet ►]'[◄ inches ►][◄ frac ►]"
// Internal value is always decimal inches (the canonical unit).
// Imperial: three boxes — feet (±1), inches (±1), fraction (±1/8)
// Metric:   single mm input (unchanged)
// Cascade rollover: 7/8 + 1/8 → +1 inch; 11" + 1" → +1 foot (and reverse)

import { forwardRef, useImperativeHandle, useRef } from 'react';

export interface FeetInchesHandle { focus: () => void; }

interface Props {
  value: number;        // decimal inches (always)
  onChange: (v: number) => void;
  min?: number;
  unit?: string;        // "'" for elevations, '"' for dimensions (display only)
  metric?: boolean;     // true = show mm
}

const IN_TO_MM = 25.4;

// Fraction eighths → display string
const FRAC_LABELS = ['', '1/8', '1/4', '3/8', '1/2', '5/8', '3/4', '7/8'];

/** Decompose decimal inches → { sign, feet, inches, eighths } */
function decompose(totalInches: number) {
  const sign  = totalInches < 0 ? -1 : 1;
  const abs   = Math.abs(totalInches);
  let eighths = Math.round(abs * 8);        // total eighths
  let feet    = Math.floor(eighths / 96);    // 96 eighths per foot
  eighths -= feet * 96;
  let inches  = Math.floor(eighths / 8);
  eighths -= inches * 8;
  return { sign, feet, inches, eighths };
}

/** Recompose { sign, feet, inches, eighths } → decimal inches */
function recompose(sign: number, feet: number, inches: number, eighths: number) {
  return sign * (feet * 12 + inches + eighths / 8);
}

const FeetInchesInput = forwardRef<FeetInchesHandle, Props>(
  function FeetInchesInput({ value, onChange, min = 0, unit = '"', metric = false }, ref) {
    const feetRef = useRef<HTMLInputElement>(null);
    const inchRef = useRef<HTMLInputElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => feetRef.current?.focus(),
    }));

    // ── Metric mode: single mm input ──────────────────────────────────────
    if (metric) {
      const mm = Math.round(value * IN_TO_MM * 10) / 10;
      const stepMm = (dir: 1 | -1) => {
        const next = Math.round((mm + dir) * 10) / 10;
        const inches = next / IN_TO_MM;
        if (inches >= min) onChange(inches);
      };
      return (
        <div className="dim3-wrap">
          <input
            ref={feetRef}
            className="dim3-box dim3-mm"
            type="number"
            value={mm}
            step={1}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v) && v / IN_TO_MM >= min) onChange(v / IN_TO_MM);
            }}
            onKeyDown={e => {
              if (e.key === 'ArrowUp')   { e.preventDefault(); stepMm(1); }
              if (e.key === 'ArrowDown') { e.preventDefault(); stepMm(-1); }
            }}
          />
          <div className="dim3-spinner">
            <button className="dim3-arrow up" onClick={() => stepMm(1)} tabIndex={-1}>▲</button>
            <button className="dim3-arrow dn" onClick={() => stepMm(-1)} tabIndex={-1}>▼</button>
          </div>
          <span className="dim3-sep">mm</span>
        </div>
      );
    }

    // ── Imperial mode: 3-box ──────────────────────────────────────────────
    const { sign, feet, inches, eighths } = decompose(value);

    const emit = (s: number, f: number, i: number, e: number) => {
      const v = recompose(s, f, i, e);
      if (v >= min) onChange(v);
    };

    // Feet arrows: ±1 foot
    const stepFeet = (dir: 1 | -1) => {
      const newFeet = feet + dir * sign;  // sign-aware: +1 means "more positive"
      if (newFeet < 0 && sign > 0) {
        // Going negative from 0' — flip sign
        emit(-1, -newFeet, inches, eighths);
      } else if (newFeet < 0 && sign < 0) {
        // Making negative value less negative
        emit(1, -newFeet, inches, eighths);
      } else {
        emit(sign, newFeet, inches, eighths);
      }
    };

    // Inches arrows: ±1 inch with rollover
    const stepInches = (dir: 1 | -1) => {
      let ni = inches + dir;
      let nf = feet;
      if (ni > 11) { ni = 0; nf++; }
      if (ni < 0)  { ni = 11; nf = Math.max(0, nf - 1); }
      emit(sign, nf, ni, eighths);
    };

    // Fraction arrows: ±1/8 with rollover
    const stepFrac = (dir: 1 | -1) => {
      let ne = eighths + dir;
      let ni = inches;
      let nf = feet;
      if (ne > 7) { ne = 0; ni++; }
      if (ne < 0) { ne = 7; ni--; }
      if (ni > 11) { ni = 0; nf++; }
      if (ni < 0)  { ni = 11; nf = Math.max(0, nf - 1); }
      emit(sign, nf, ni, ne);
    };

    // Direct typing in feet box
    const onFeetChange = (raw: string) => {
      const v = parseInt(raw, 10);
      if (isNaN(v)) return;
      if (v < 0) {
        emit(-1, Math.abs(v), inches, eighths);
      } else {
        emit(v === 0 && sign < 0 ? -1 : 1, v, inches, eighths);
      }
    };

    // Direct typing in inches box
    const onInchChange = (raw: string) => {
      const v = parseInt(raw, 10);
      if (isNaN(v) || v < 0 || v > 11) return;
      emit(sign, feet, v, eighths);
    };

    const unitSuffix = unit === "'" ? "'" : '"';

    return (
      <div className="dim3-wrap">
        <input
          ref={feetRef}
          className="dim3-box dim3-feet"
          type="number"
          value={sign < 0 ? -feet : feet}
          onChange={e => onFeetChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'ArrowUp')   { e.preventDefault(); stepFeet(1); }
            if (e.key === 'ArrowDown') { e.preventDefault(); stepFeet(-1); }
          }}
        />
        <div className="dim3-spinner">
          <button className="dim3-arrow up" onClick={() => stepFeet(1)} tabIndex={-1}>▲</button>
          <button className="dim3-arrow dn" onClick={() => stepFeet(-1)} tabIndex={-1}>▼</button>
        </div>
        <span className="dim3-sep">'</span>
        <input
          ref={inchRef}
          className="dim3-box dim3-inch"
          type="number"
          value={inches}
          min={0} max={11}
          onChange={e => onInchChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'ArrowUp')   { e.preventDefault(); stepInches(1); }
            if (e.key === 'ArrowDown') { e.preventDefault(); stepInches(-1); }
          }}
        />
        <div className="dim3-spinner">
          <button className="dim3-arrow up" onClick={() => stepInches(1)} tabIndex={-1}>▲</button>
          <button className="dim3-arrow dn" onClick={() => stepInches(-1)} tabIndex={-1}>▼</button>
        </div>
        <span className="dim3-frac">{FRAC_LABELS[eighths] || '0'}</span>
        <div className="dim3-spinner">
          <button className="dim3-arrow up" onClick={() => stepFrac(1)} tabIndex={-1}>▲</button>
          <button className="dim3-arrow dn" onClick={() => stepFrac(-1)} tabIndex={-1}>▼</button>
        </div>
        <span className="dim3-sep">{unitSuffix}</span>
      </div>
    );
  }
);

export default FeetInchesInput;
export { formatMetric } from '../utils/imperial';
